import { useEffect } from "react";
import { toast } from "sonner";
import { API_BASE } from "@/api/http";
import type { StreamHelloDto, WorkspaceChangeDto } from "@/api/types";
import { byClient, clientPrefix } from "@/lib/format";
import { useStore } from "@/model/store";

// Live updates (API plan §8.11): `GET /w/:ws/events/stream` opens with `hello` (the workspace's revision)
// and sends `revision` after every change. The app refreshes whenever the server is ahead, and says so
// when the change came from somewhere else (Claude over MCP, or another member).
//
// EventSource reconnects by itself after a dropped connection; each reconnect gets a new `hello`, so a
// change made while it was down is caught then. The API allows six streams per user, so a tab that has
// been hidden for a while closes its stream and reopens it when it is shown again.

const HIDDEN_CLOSE_MS = 5 * 60 * 1000;

function parse<T>(e: MessageEvent<string>): T | null {
	try {
		return JSON.parse(e.data) as T;
	} catch {
		return null;
	}
}

function catchUp(revision: number) {
	const { revision: have, refresh } = useStore.getState();
	if (have !== null && revision <= have) return false;
	void refresh(revision);
	return true;
}

/** What to say about a change made elsewhere, or null for the user's own changes in this app. */
export function describeChange(ev: WorkspaceChangeDto, myUserId: string | null): string | null {
	if (byClient(ev.origin)) return `${clientPrefix(ev.origin, ev.actor.client_name)}${ev.summary}`;
	if (ev.origin === "app" && ev.actor.user_id && ev.actor.user_id !== myUserId) return ev.summary;
	return null;
}

export function useLiveUpdates(ws: string, openActivity: (changesetId: string) => void) {
	useEffect(() => {
		if (typeof EventSource === "undefined") return;
		let es: EventSource | null = null;
		let hiddenTimer: ReturnType<typeof setTimeout> | null = null;

		const open = () => {
			if (es) return;
			es = new EventSource(`${API_BASE}/w/${encodeURIComponent(ws)}/events/stream`, { withCredentials: true });
			es.addEventListener("hello", ((e: MessageEvent<string>) => {
				const hello = parse<StreamHelloDto>(e);
				if (hello) catchUp(hello.revision);
			}) as EventListener);
			es.addEventListener("revision", ((e: MessageEvent<string>) => {
				const ev = parse<WorkspaceChangeDto>(e);
				if (!ev || !catchUp(ev.revision)) return;
				const text = describeChange(ev, useStore.getState().user?.id ?? null);
				const id = ev.changeset_id;
				if (text) toast(text, id ? { action: { label: "See what changed", onClick: () => openActivity(id) } } : undefined);
			}) as EventListener);
		};
		const close = () => {
			es?.close();
			es = null;
		};
		const onVisibility = () => {
			if (document.visibilityState === "hidden") {
				hiddenTimer = setTimeout(close, HIDDEN_CLOSE_MS);
			} else {
				if (hiddenTimer) clearTimeout(hiddenTimer);
				hiddenTimer = null;
				open();
			}
		};

		open();
		document.addEventListener("visibilitychange", onVisibility);
		return () => {
			document.removeEventListener("visibilitychange", onVisibility);
			if (hiddenTimer) clearTimeout(hiddenTimer);
			close();
		};
	}, [ws, openActivity]);
}
